import { motion } from 'motion/react'
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react'
import { Link } from 'react-router'
import { cn } from '#app/utils/misc'
import { useReducedMotionSafe } from '#app/utils/site/scene-clock'
import { isExternalHref } from '#app/utils/site/site-links'

/**
 * Small shared pieces for the public website. Everything else under
 * app/components/site builds on these, so the look stays in one place.
 */

/** The 1240px content column with the reference gutters (40px, 18px on phones). */
export function Container({
	className,
	...props
}: ComponentPropsWithoutRef<'div'>) {
	return (
		<div
			className={cn(
				'mx-auto w-full max-w-[1240px] px-[18px] sm:px-10',
				className,
			)}
			{...props}
		/>
	)
}

/** Big condensed uppercase display type. Size it with a text class. */
export function BigHeading<T extends ElementType = 'h2'>({
	as,
	className,
	...props
}: { as?: T; className?: string } & Omit<ComponentPropsWithoutRef<T>, 'as'>) {
	const Tag = (as ?? 'h2') as ElementType
	return <Tag className={cn('tt-big', className)} {...props} />
}

export function ArrowIcon({ className }: { className?: string }) {
	return (
		<svg
			className={cn('h-[18px] w-[18px]', className)}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.9}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M5 12h13M13 6.5l5.5 5.5L13 17.5" />
		</svg>
	)
}

/** The green circle-check used across the product scenes. */
export function CheckIcon({ className }: { className?: string }) {
	return (
		<svg
			className={cn('h-6 w-6', className)}
			viewBox="0 0 24 24"
			aria-hidden="true"
		>
			<circle cx="12" cy="12" r="10.5" fill="#16A34A" />
			<path
				d="M7.6 12.4l2.9 2.9 5.9-6.2"
				fill="none"
				stroke="#fff"
				strokeWidth={2.1}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

type PillVariant = 'ink' | 'white' | 'ghost'
type PillSize = 'sm' | 'lg'

const pillVariant: Record<PillVariant, string> = {
	ink: 'bg-tt-ink text-white',
	white: 'bg-white text-tt-ink',
	ghost: 'border-[1.5px] border-tt-ink bg-transparent text-tt-ink',
}
const pillSize: Record<PillSize, string> = {
	sm: 'px-[18px] py-[9px] text-[14px]',
	lg: 'px-8 py-[17px] text-[17px]',
}

/**
 * The rounded CTA. Internal hrefs render a client-side <Link>; external ones
 * open in a new tab. Pass `arrow` for the trailing arrow.
 */
export function Pill({
	href,
	variant = 'ink',
	size = 'lg',
	arrow = false,
	className,
	children,
	...rest
}: {
	href: string
	variant?: PillVariant
	size?: PillSize
	arrow?: boolean
	className?: string
	children: ReactNode
} & Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'children'>) {
	const classes = cn(
		'inline-flex items-center gap-2 whitespace-nowrap rounded-full font-semibold leading-tight transition-[transform,opacity] duration-200 ease-tt hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2',
		pillVariant[variant],
		pillSize[size],
		className,
	)
	if (isExternalHref(href)) {
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className={classes}
				{...rest}
			>
				{children}
				{arrow ? <ArrowIcon /> : null}
			</a>
		)
	}
	return (
		<Link to={href} className={classes} {...rest}>
			{children}
			{arrow ? <ArrowIcon /> : null}
		</Link>
	)
}

/** The underlined secondary link next to a Pill. */
export function TextLink({
	href,
	className,
	children,
	...rest
}: {
	href: string
	className?: string
	children: ReactNode
} & Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'children'>) {
	const classes = cn(
		'text-[17px] font-semibold leading-tight underline decoration-2 underline-offset-[6px] hover:decoration-[3px]',
		className,
	)
	if (href.startsWith('#') || isExternalHref(href)) {
		return (
			<a href={href} className={classes} {...rest}>
				{children}
			</a>
		)
	}
	return (
		<Link to={href} className={classes} {...rest}>
			{children}
		</Link>
	)
}

/**
 * Fade-and-rise once when scrolled into view (the reference's `.rv`). The
 * markup is identical on the server and the client; reduced motion only makes
 * the transition instant, so hydration never strands the hidden state.
 */
export function Reveal({
	className,
	children,
	delay = 0,
}: {
	className?: string
	children: ReactNode
	delay?: number
}) {
	const reduced = useReducedMotionSafe()
	return (
		<motion.div
			className={className}
			initial={{ opacity: 0, y: 26 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.15 }}
			transition={
				reduced
					? { duration: 0 }
					: { duration: 0.8, ease: [0.19, 0.86, 0.28, 1], delay }
			}
		>
			{children}
		</motion.div>
	)
}

/** The soft white "mini" card used inside the product tiles. */
export function MiniCard({
	className,
	...props
}: ComponentPropsWithoutRef<'div'>) {
	return (
		<div
			className={cn(
				'w-full max-w-[380px] rounded-[22px] bg-white p-[22px] shadow-tt-card',
				className,
			)}
			{...props}
		/>
	)
}
